import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { Alert, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignInButton } from '../../../SignInButton';
import { UserContext } from '../../../UserContext';
import {
  OnboardingStatusValue,
  TwilioOnboardingListItem,
} from '../../../types/twilio-onboarding';
import { listOnboardings } from '../../../utils/twilio-onboarding-api';
import { getStatusTagColor } from '../../../components/twilio-onboarding/onboarding-logic';

const { Title } = Typography;

const columns: ColumnsType<TwilioOnboardingListItem> = [
  {
    title: 'Client ID',
    dataIndex: 'client_id',
    key: 'client_id',
    width: 100,
  },
  {
    title: 'Brand Name',
    dataIndex: 'brand_name',
    key: 'brand_name',
    render: (val: string) => val || '—',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: OnboardingStatusValue) => (
      <Tag color={getStatusTagColor(status)}>{status.replace(/_/g, ' ')}</Tag>
    ),
  },
  {
    title: 'Rejection',
    key: 'rejection',
    render: (_: unknown, record: TwilioOnboardingListItem) =>
      record.rejection_step ? (
        <Tag color="red">
          {record.rejection_step}:{' '}
          {(record.rejection_details?.length ?? 0) > 60
            ? record.rejection_details?.substring(0, 60) + '...'
            : record.rejection_details}
        </Tag>
      ) : (
        '—'
      ),
  },
  {
    title: 'Updated',
    dataIndex: 'updated_at',
    key: 'updated_at',
    render: (val: string) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
];

export function TwilioOnboardingDashboardPage({ apiUrl }: { apiUrl: string }) {
  const user = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [data, setData] = useState<TwilioOnboardingListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  useEffect(() => {
    void loadData();
  }, [user?.accessToken, statusFilter]);

  const loadData = async () => {
    if (!user) {
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const items = await listOnboardings(
        apiUrl,
        user.accessToken,
        statusFilter,
      );
      setData(items);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load onboardings';
      if (msg.includes('404') || msg.includes('501')) {
        setError(
          'The onboarding list endpoint is not yet available on the backend.',
        );
        setData([]);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthenticatedTemplate>
        <Title level={3} style={{ marginBottom: 24, color: '#333' }}>
          Twilio Onboarding Dashboard
        </Title>

        <Space style={{ marginBottom: 16 }}>
          <Select
            allowClear
            onChange={setStatusFilter}
            placeholder="Filter by status"
            style={{ width: 200 }}
            value={statusFilter}
          >
            <Select.Option value="pending_profile">
              Pending Profile
            </Select.Option>
            <Select.Option value="pending_brand">Pending Brand</Select.Option>
            <Select.Option value="pending_campaign">
              Pending Campaign
            </Select.Option>
            <Select.Option value="pending_number">Pending Number</Select.Option>
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="rejected">Rejected</Select.Option>
          </Select>
        </Space>

        {error && (
          <Alert
            closable
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
            type="info"
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            locale={{ emptyText: 'No onboarding records found.' }}
            onRow={record => ({
              onClick: () =>
                navigate(`/client/${record.client_id}/twilio-onboarding`),
              style: { cursor: 'pointer' },
            })}
            rowKey="client_id"
          />
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <SignInButton />
      </UnauthenticatedTemplate>
    </>
  );
}
